/*
 * Seven Pages: gift wheel, help panel and cart prize actions.
 *
 * Gift wheel ([data-sp-wheel], snippets/sp-spin-wheel.liquid)
 * - One free spin per shopper. A new spin unlocks after an order (the cart token changes once a
 *   cart is checked out) or after 30 days. No purchase is needed to spin.
 * - Prizes are real discount codes created in Shopify. Codes are attached to the cart, and the
 *   prize is saved as the hidden cart attribute `_sp_prize` so the cart drawer can show it
 *   (snippets/sp-cart-boost.liquid).
 *
 * Help panel ([data-sp-help], snippets/sp-help.liquid)
 * - Instant answers from the store's own policies, matched by keywords, with a hand-off to email.
 * - Hides itself when Shopify Inbox is installed, because Inbox then provides the chat.
 *
 * Cart updates go through Shopify.actions.updateCart (Horizon's standard actions), so the cart
 * drawer refreshes the same way it does for the theme's own buttons. If that API is missing,
 * the AJAX cart API is used instead.
 */

const STORE_KEY = 'sp_wheel_v1';
const SESSION_KEY = 'sp_wheel_seen';
/* Codes this browser has won. Kept when a new spin unlocks, because codes work once per customer. */
const WON_KEY = 'sp_wheel_won';
const RESET_MS = 30 * 24 * 60 * 60 * 1000;
const SPIN_MS = 4600;

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** 'product', 'cart', 'index', ... from <main data-template> in layout/theme.liquid. */
function pageType() {
  const main = document.querySelector('main[data-template]');
  let name = main ? main.getAttribute('data-template') : '';
  if (!name) {
    try {
      /* Horizon declares `const Theme` in a classic script: a global binding, but not on window. */
      name = (typeof Theme !== 'undefined' && Theme.template && Theme.template.name) || '';
    } catch (error) {
      name = '';
    }
  }
  return String(name || '').split('.')[0];
}

/**
 * Shows a floating button. On phones on a product page it waits until the shopper has scrolled
 * past the main Add to cart button, so the buttons never sit over the gallery and the title.
 */
function revealFab(fab) {
  const show = () => fab.classList.add('is-visible');
  const onPhone = window.matchMedia('(max-width: 749px)').matches;
  const buy =
    onPhone && pageType() === 'product'
      ? Array.from(document.querySelectorAll('main [name="add"], main .add-to-cart-button')).find(
          (node) => !node.closest('.sticky-add-to-cart')
        )
      : null;
  if (!buy) {
    window.setTimeout(show, 2500);
    return;
  }
  let queued = false;
  const check = () => {
    queued = false;
    if (buy.getBoundingClientRect().bottom < 0) {
      show();
      window.removeEventListener('scroll', onScroll);
    }
  };
  const onScroll = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(check);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  check();
}

/* ---------------------------------------------------------------- storage */

function readJSON(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeJSON(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    /* private mode or blocked storage: the wheel still works for this page view */
  }
}

function wonCodes() {
  const list = readJSON(window.localStorage, WON_KEY);
  return Array.isArray(list) ? list.map((code) => String(code).toUpperCase()) : [];
}

/* ------------------------------------------------------------------- cart */

function baseToken(token) {
  return String(token || '').split('?')[0];
}

async function fetchCart() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  }
}

function jsonPost(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
}

async function currentCodes() {
  const actions = window.Shopify && window.Shopify.actions;
  try {
    if (actions && typeof actions.getCart === 'function') {
      const result = await actions.getCart();
      const codes = result && result.cart && result.cart.discountCodes;
      if (Array.isArray(codes)) return codes.map((entry) => entry.code).filter(Boolean);
    }
  } catch (error) {
    /* fall through to the AJAX cart */
  }
  const cart = await fetchCart();
  const codes = (cart && cart.discount_codes) || [];
  return codes.map((entry) => (typeof entry === 'string' ? entry : entry.code)).filter(Boolean);
}

/**
 * Adds lines and/or replaces discount codes. Returns { ok, message }.
 * @param {{ lines?: Array<{merchandiseId?: string|number, id?: string, quantity: number}>, discountCodes?: string[] }} payload
 */
async function updateCart(payload) {
  const actions = window.Shopify && window.Shopify.actions;
  if (actions && typeof actions.updateCart === 'function') {
    try {
      const clean = {};
      if (payload.lines) {
        clean.lines = payload.lines.map((line) =>
          line.id ? { id: String(line.id), quantity: line.quantity } : { merchandiseId: String(line.merchandiseId), quantity: line.quantity }
        );
      }
      if (payload.discountCodes) clean.discountCodes = payload.discountCodes;
      const result = await actions.updateCart(clean);
      const errors = (result && result.userErrors) || [];
      if (errors.length) return { ok: false, message: errors[0].message };
      return { ok: true };
    } catch (error) {
      /* fall back to the AJAX API below */
    }
  }

  try {
    const adds = (payload.lines || [])
      .filter((line) => !line.id)
      .map((line) => ({ id: Number(String(line.merchandiseId).split('/').pop()), quantity: line.quantity }));
    if (adds.length) {
      const response = await jsonPost('/cart/add.js', { items: adds });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        return { ok: false, message: data.description || 'That could not be added.' };
      }
    }
    for (const line of (payload.lines || []).filter((entry) => entry.id)) {
      await jsonPost('/cart/change.js', { id: String(line.id), quantity: line.quantity });
    }
    if (payload.discountCodes) {
      await jsonPost('/cart/update.js', { discount: payload.discountCodes.join(',') });
    }
    return { ok: true, reload: true };
  } catch (error) {
    return { ok: false, message: 'Please check your connection and try again.' };
  }
}

async function setPrizeAttribute(code) {
  try {
    await jsonPost('/cart/update.js', { attributes: { _sp_prize: code } });
  } catch (error) {
    /* the cart card is a convenience; the code itself is already on the cart */
  }
}

/** Puts `code` on the cart, replacing any earlier wheel code. */
async function applyCode(code, lines) {
  const existing = await currentCodes();
  const codes = existing.filter((entry) => !/^SPIN/i.test(entry) || entry.toUpperCase() === code.toUpperCase());
  if (!codes.some((entry) => entry.toUpperCase() === code.toUpperCase())) codes.push(code);
  return updateCart({ lines, discountCodes: codes });
}

function openCartDrawer() {
  const actions = window.Shopify && window.Shopify.actions;
  if (actions && actions.openCart && typeof actions.openCart === 'function') {
    actions.openCart();
    return;
  }
  window.location.assign('/cart');
}

/* ---------------------------------------------------------------- confetti */

function confetti(host) {
  if (reduceMotion() || !host || typeof host.animate !== 'function') return;
  const colors = ['#C8A36A', '#E2C48E', '#1C1917', '#F3EBDF', '#A9824A'];
  for (let index = 0; index < 28; index += 1) {
    const piece = document.createElement('span');
    piece.className = 'sp-confetti';
    piece.style.background = colors[index % colors.length];
    host.appendChild(piece);
    const angle = Math.random() * Math.PI * 2;
    const distance = 90 + Math.random() * 140;
    const x = Math.cos(angle) * distance;
    const y = Math.sin(angle) * distance - 60;
    const animation = piece.animate(
      [
        { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 },
        { transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y + 120}px)) rotate(${Math.random() * 720 - 360}deg)`, opacity: 0 },
      ],
      { duration: 1300 + Math.random() * 500, easing: 'cubic-bezier(.2,.7,.3,1)' }
    );
    animation.onfinish = () => piece.remove();
  }
}

/* ------------------------------------------------------------- gift wheel */

function initWheel() {
  const root = document.querySelector('[data-sp-wheel]');
  const configNode = document.getElementById('sp-wheel-config');
  if (!root || !configNode || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';

  let config;
  try {
    config = JSON.parse(configNode.textContent || '{}');
  } catch (error) {
    return;
  }
  const slices = Array.isArray(config.slices) ? config.slices : [];
  const prizes = config.prizes || {};
  const lockets = (config.lockets || []).map(Number);
  if (!slices.length) return;

  const dialog = root.querySelector('[data-sp-wheel-dialog]');
  const fab = root.querySelector('[data-sp-wheel-open]');
  const fabLabel = root.querySelector('[data-sp-fab-label]');
  const disc = root.querySelector('[data-sp-disc]');
  const form = root.querySelector('form');
  const email = root.querySelector('input[type="email"]');
  const spinButton = root.querySelector('[data-sp-spin]');
  const playView = root.querySelector('[data-view="play"]');
  const resultView = root.querySelector('[data-view="result"]');
  const resultTitle = root.querySelector('[data-sp-result-title]');
  const resultBody = root.querySelector('[data-sp-result-body]');
  const codeWrap = root.querySelector('[data-sp-code-wrap]');
  const codeValue = root.querySelector('[data-sp-code]');
  const copyButton = root.querySelector('[data-sp-copy]');
  const status = root.querySelector('[data-sp-status]');
  const primary = root.querySelector('[data-sp-primary]');
  const confettiHost = root.querySelector('[data-sp-confetti]');
  if (!(dialog instanceof HTMLDialogElement) || !fab || !disc || !form || !primary) return;

  const sliceAngle = 360 / slices.length;
  let state = readJSON(window.localStorage, STORE_KEY);
  let spinning = false;
  let cartToken = '';
  let cartItems = [];

  /* Draw the wheel once. Text runs from the rim toward the hub so it reads at any angle. */
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('viewBox', '0 0 200 200');
  svg.setAttribute('aria-hidden', 'true');
  slices.forEach((slice, index) => {
    const start = ((index * sliceAngle - 90) * Math.PI) / 180;
    const end = (((index + 1) * sliceAngle - 90) * Math.PI) / 180;
    const x1 = 100 + 96 * Math.cos(start);
    const y1 = 100 + 96 * Math.sin(start);
    const x2 = 100 + 96 * Math.cos(end);
    const y2 = 100 + 96 * Math.sin(end);
    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('d', `M100 100 L${x1.toFixed(2)} ${y1.toFixed(2)} A96 96 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`);
    const tone = slice.tone || (index % 2 ? 'light' : 'dark');
    path.setAttribute('class', `sp-slice sp-slice--${tone}`);
    svg.appendChild(path);

    const mid = index * sliceAngle + sliceAngle / 2;
    /* Labels on the left half are turned around so none read upside down at rest. */
    const flip = mid > 180;
    const text = document.createElementNS(svgNS, 'text');
    text.setAttribute('x', '100');
    text.setAttribute('y', '100');
    text.setAttribute('class', `sp-slice-text sp-slice-text--${tone}`);
    text.setAttribute('transform', flip ? `rotate(${mid + 90} 100 100) translate(-86 0)` : `rotate(${mid - 90} 100 100) translate(86 0)`);
    text.setAttribute('text-anchor', flip ? 'start' : 'end');
    text.setAttribute('dominant-baseline', 'middle');
    const lines = String(slice.label || '').split('|');
    lines.forEach((line, lineIndex) => {
      const tspan = document.createElementNS(svgNS, 'tspan');
      tspan.setAttribute('x', '100');
      tspan.setAttribute('dy', lineIndex === 0 ? `${-(lines.length - 1) * 4.5}` : '9');
      tspan.textContent = line;
      text.appendChild(tspan);
    });
    svg.appendChild(text);
  });
  const rim = document.createElementNS(svgNS, 'circle');
  rim.setAttribute('cx', '100');
  rim.setAttribute('cy', '100');
  rim.setAttribute('r', '96');
  rim.setAttribute('class', 'sp-rim');
  svg.appendChild(rim);
  for (let index = 0; index < slices.length * 2; index += 1) {
    const angle = ((index * (sliceAngle / 2) - 90) * Math.PI) / 180;
    const bulb = document.createElementNS(svgNS, 'circle');
    bulb.setAttribute('cx', (100 + 96 * Math.cos(angle)).toFixed(2));
    bulb.setAttribute('cy', (100 + 96 * Math.sin(angle)).toFixed(2));
    bulb.setAttribute('r', '2.2');
    bulb.setAttribute('class', `sp-bulb${index % 2 ? ' sp-bulb--alt' : ''}`);
    svg.appendChild(bulb);
  }
  disc.appendChild(svg);

  function hasLocket(items) {
    return items.some((item) => lockets.includes(Number(item.product_id)));
  }

  function hasVariant(items, variantId) {
    return items.some((item) => Number(item.variant_id) === Number(variantId));
  }

  async function refreshCart() {
    const cart = await fetchCart();
    if (!cart) return;
    cartToken = baseToken(cart.token);
    cartItems = cart.items || [];
    if (state && state.cart && state.cart !== cartToken && state.items > 0 && cartItems.length === 0) {
      /* The cart this shopper spun with was checked out: a new spin is available. */
      state = null;
      writeJSON(window.localStorage, STORE_KEY, null);
    } else if (state && state.cart === cartToken && state.items !== cartItems.length) {
      state.items = cartItems.length;
      writeJSON(window.localStorage, STORE_KEY, state);
    }
  }

  function eligible() {
    if (!state || !state.at) return true;
    return Date.now() - state.at > RESET_MS;
  }

  function prizeFor(id) {
    return prizes[id] || prizes.none || { kind: 'none', title: 'So close!', body: '' };
  }

  function updateFab() {
    if (eligible()) {
      fab.hidden = false;
      fab.dataset.mode = 'spin';
      if (fabLabel) fabLabel.textContent = fabLabel.dataset.spinText || 'Spin for a gift';
      return;
    }
    const prize = state ? prizeFor(state.prize) : null;
    if (prize && prize.kind !== 'none' && !state.used) {
      fab.hidden = false;
      fab.dataset.mode = 'prize';
      if (fabLabel) fabLabel.textContent = fabLabel.dataset.prizeText || 'Your gift';
    } else {
      fab.hidden = true;
    }
  }

  function setStatus(message) {
    if (status) status.textContent = message || '';
  }

  function showPlay() {
    if (playView) playView.hidden = false;
    if (resultView) resultView.hidden = true;
  }

  function renderResult(prizeId, { celebrate = false } = {}) {
    const prize = prizeFor(prizeId);
    if (playView) playView.hidden = true;
    if (resultView) resultView.hidden = false;
    resultView.dataset.kind = prize.kind;
    if (resultTitle) resultTitle.textContent = prize.title || '';
    if (resultBody) resultBody.textContent = prize.body || '';
    if (codeWrap && codeValue) {
      codeWrap.hidden = !prize.code;
      codeValue.textContent = prize.code || '';
    }
    setStatus('');

    const locketInCart = hasLocket(cartItems);
    primary.hidden = false;
    primary.dataset.action = 'close';
    if (prize.kind === 'code') {
      if (cartItems.length) {
        primary.textContent = 'View my cart';
        primary.dataset.action = 'cart';
      } else if (pageType() === 'product') {
        primary.textContent = 'Back to the locket';
      } else {
        primary.textContent = 'Shop lockets';
        primary.dataset.action = 'shop';
      }
    } else if (prize.kind === 'gift' || prize.kind === 'mystery') {
      const already = hasVariant(cartItems, prize.variant);
      if (already) {
        primary.textContent = 'View my cart';
        primary.dataset.action = 'cart';
      } else if (locketInCart) {
        primary.textContent = prize.kind === 'gift' ? 'Add my free bracelet set' : 'Add my mystery gift';
        primary.dataset.action = 'add';
      } else if (pageType() === 'product') {
        primary.textContent = 'Choose this locket first';
      } else {
        primary.textContent = 'Shop lockets';
        primary.dataset.action = 'shop';
      }
    } else {
      primary.textContent = 'Keep shopping';
    }

    if (resultTitle) {
      window.setTimeout(() => resultTitle.focus({ preventScroll: true }), 30);
    }
    if (celebrate && prize.kind !== 'none') confetti(confettiHost);
  }

  function open(trigger) {
    if (dialog.open || spinning) return;
    writeJSON(window.sessionStorage, SESSION_KEY, { at: Date.now(), trigger });
    if (eligible()) {
      showPlay();
    } else if (state) {
      renderResult(state.prize);
    }
    try {
      dialog.showModal();
    } catch (error) {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('sp-modal-open');
    if (eligible() && email && trigger === 'fab') {
      window.setTimeout(() => email.focus({ preventScroll: true }), 60);
    }
    publish('sp_gift_wheel_open', { trigger });
  }

  function close() {
    if (spinning) return;
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('sp-modal-open');
    updateFab();
  });
  dialog.addEventListener('cancel', (event) => {
    if (spinning) event.preventDefault();
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });
  root.querySelectorAll('[data-sp-close]').forEach((button) => button.addEventListener('click', close));
  fab.addEventListener('click', () => open('fab'));

  /* A later spin (after an order, or after 30 days) skips prizes this shopper already won,
     because each code works once per customer. The odds text in the wheel says so. */
  function pickIndex() {
    const won = wonCodes();
    const weights = slices.map((slice) => {
      const code = String(prizeFor(slice.id).code || '').toUpperCase();
      return code && won.includes(code) ? 0 : Math.max(Number(slice.weight) || 0, 0);
    });
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (total <= 0) {
      const blank = slices.findIndex((slice) => !prizeFor(slice.id).code);
      return blank >= 0 ? blank : 0;
    }
    const random = new Uint32Array(1);
    (window.crypto || window.msCrypto).getRandomValues(random);
    let roll = (random[0] / 4294967296) * total;
    let last = 0;
    for (let index = 0; index < slices.length; index += 1) {
      if (weights[index] <= 0) continue;
      last = index;
      roll -= weights[index];
      if (roll < 0) return index;
    }
    return last;
  }

  function subscribe() {
    try {
      const data = new FormData(form);
      fetch(form.action, { method: 'POST', body: data, headers: { Accept: 'text/html' } }).catch(() => {});
    } catch (error) {
      /* email capture is best effort */
    }
  }

  async function awardPrize(prizeId) {
    const prize = prizeFor(prizeId);
    state = { at: Date.now(), prize: prizeId, cart: cartToken, items: cartItems.length, used: false };
    writeJSON(window.localStorage, STORE_KEY, state);
    publish('sp_gift_wheel_spin', { prize: prizeId });
    if (!prize.code) return;
    const won = wonCodes();
    if (!won.includes(prize.code.toUpperCase())) writeJSON(window.localStorage, WON_KEY, won.concat(prize.code.toUpperCase()));
    await setPrizeAttribute(prize.code);
    if (prize.kind !== 'code') {
      /* The gift codes only work with a locket, so they go on the cart together with the gift
         line (wheel button or cart card). Attaching them earlier would show a "code not valid"
         notice at checkout for carts without a locket. */
      await refreshCart();
      state.cart = cartToken;
      writeJSON(window.localStorage, STORE_KEY, state);
      if (!hasLocket(cartItems)) setStatus('Saved. Your cart will offer it once a locket is in.');
      return;
    }
    const result = await applyCode(prize.code);
    await refreshCart();
    state.cart = cartToken;
    writeJSON(window.localStorage, STORE_KEY, state);
    if (result.ok) {
      setStatus(prize.kind === 'code' ? 'Saved to your cart. It comes off at checkout.' : 'Saved to your cart.');
    } else {
      setStatus(`Use code ${prize.code} at checkout.`);
    }
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (spinning) return;
    if (!eligible()) {
      renderResult(state && state.prize);
      return;
    }
    if (email && !email.checkValidity()) {
      email.reportValidity();
      return;
    }
    spinning = true;
    dialog.classList.add('is-spinning');
    if (spinButton) {
      spinButton.disabled = true;
      spinButton.textContent = spinButton.dataset.spinningText || 'Good luck...';
    }
    subscribe();

    const index = pickIndex();
    const centre = index * sliceAngle + sliceAngle / 2;
    const jitter = (Math.random() - 0.5) * sliceAngle * 0.6;
    const turns = reduceMotion() ? 1 : 6;
    const target = turns * 360 + (360 - centre) + jitter;
    disc.style.transition = reduceMotion() ? 'none' : `transform ${SPIN_MS}ms cubic-bezier(.12,.72,.13,1)`;
    disc.style.transform = `rotate(${target}deg)`;

    await new Promise((resolve) => window.setTimeout(resolve, reduceMotion() ? 150 : SPIN_MS + 150));
    spinning = false;
    dialog.classList.remove('is-spinning');
    const prizeId = slices[index].id;
    renderResult(prizeId, { celebrate: true });
    awardPrize(prizeId).catch(() => setStatus(''));
    updateFab();
  });

  copyButton?.addEventListener('click', async () => {
    const code = codeValue ? codeValue.textContent : '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      copyButton.textContent = 'Copied';
    } catch (error) {
      copyButton.textContent = code;
    }
    window.setTimeout(() => {
      copyButton.textContent = 'Copy';
    }, 2000);
  });

  primary.addEventListener('click', async () => {
    const action = primary.dataset.action;
    const prize = state ? prizeFor(state.prize) : null;
    if (action === 'cart') {
      close();
      openCartDrawer();
      return;
    }
    if (action === 'shop') {
      window.location.assign(config.shopUrl || '/collections/all');
      return;
    }
    if (action === 'add' && prize && prize.variant) {
      primary.disabled = true;
      primary.setAttribute('aria-busy', 'true');
      close();
      const result = await applyCode(prize.code, [{ merchandiseId: prize.variant, quantity: 1 }]);
      primary.disabled = false;
      primary.removeAttribute('aria-busy');
      if (!result.ok) {
        open('error');
        setStatus(result.message || 'That could not be added. Please try again.');
      } else if (result.reload) {
        window.location.assign('/cart');
      }
      return;
    }
    close();
  });

  /* Auto-open once per session, never on top of another dialog, on the cart page or over a full cart. */
  function anotherDialogOpen() {
    return Array.from(document.querySelectorAll('dialog[open]')).some((node) => node !== dialog);
  }

  function maybeAutoOpen(trigger) {
    if (!eligible() || dialog.open) return;
    if (readJSON(window.sessionStorage, SESSION_KEY)) return;
    if (anotherDialogOpen()) return;
    if (pageType() === 'cart') return;
    if (cartItems.length) return;
    open(trigger);
  }

  refreshCart().then(() => {
    updateFab();
    root.hidden = false;
    revealFab(fab);

    /* Opens by itself once per visit, 40 seconds in, and never for a shopper who already has a cart. */
    window.setTimeout(() => maybeAutoOpen('timer'), 40000);

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', (event) => {
        if (!event.relatedTarget && event.clientY <= 0) maybeAutoOpen('exit');
      });
    }
  });

  /* After any cart change, re-check whether the saved prize was used up by an order. */
  onCartUpdate(() => {
    refreshCart().then(updateFab);
  });
}

/* ------------------------------------------------------------- help panel */

const STOP_WORDS = new Set(
  'how does what when will can the and you your for are this that with have has get there any about from our its can not much many long'.split(' ')
);

function stem(word) {
  let value = word;
  if (value.length > 5 && value.endsWith('ing')) value = value.slice(0, -3);
  else if (value.length > 4 && value.endsWith('ed')) value = value.slice(0, -2);
  else if (value.length > 4 && value.endsWith('es')) value = value.slice(0, -2);
  else if (value.length > 3 && value.endsWith('s') && !value.endsWith('ss')) value = value.slice(0, -1);
  if (/(bb|dd|ff|gg|ll|mm|nn|pp|rr|tt)$/.test(value)) value = value.slice(0, -1);
  return value;
}

function tokenize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2 && !STOP_WORDS.has(word))
    .map(stem);
}

function initHelp() {
  const root = document.querySelector('[data-sp-help]');
  if (!root || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';

  const dialog = root.querySelector('[data-sp-help-dialog]');
  const fab = root.querySelector('[data-sp-help-open]');
  const form = root.querySelector('[data-sp-help-form]');
  const input = root.querySelector('[data-sp-help-input]');
  const answer = root.querySelector('[data-sp-help-answer]');
  const items = Array.from(root.querySelectorAll('[data-sp-faq]'));
  const mailBase = root.dataset.mail || '';
  if (!(dialog instanceof HTMLDialogElement) || !fab) return;

  const index = items.map((item) => ({
    item,
    words: new Set(tokenize(`${item.dataset.keywords || ''} ${item.querySelector('summary')?.textContent || ''}`)),
  }));

  function open() {
    try {
      dialog.showModal();
    } catch (error) {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('sp-modal-open');
    window.setTimeout(() => input?.focus({ preventScroll: true }), 60);
    publish('sp_help_open', {});
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  fab.addEventListener('click', open);
  root.querySelectorAll('[data-sp-close]').forEach((button) => button.addEventListener('click', close));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });
  dialog.addEventListener('close', () => document.documentElement.classList.remove('sp-modal-open'));

  function best(question) {
    const words = tokenize(question);
    let top = null;
    let topScore = 0;
    for (const entry of index) {
      let score = 0;
      for (const word of words) if (entry.words.has(word)) score += 1;
      if (score > topScore) {
        top = entry;
        topScore = score;
      }
    }
    return topScore > 0 ? top : null;
  }

  function mailLink(question) {
    const body = question ? `\n\nMy question: ${question}` : '';
    return `${mailBase}${mailBase.includes('?') ? '&' : '?'}body=${encodeURIComponent(`Hi Seven Pages,${body}\n\nOrder number (if you have one): `)}`;
  }

  function show(entry, question) {
    if (!answer) return;
    answer.replaceChildren();
    const bubble = document.createElement('div');
    bubble.className = 'sp-help__bubble';
    if (entry) {
      const heading = document.createElement('p');
      heading.className = 'sp-help__bubble-title';
      heading.textContent = entry.item.querySelector('summary')?.textContent?.trim() || '';
      const content = entry.item.querySelector('.sp-help__a')?.cloneNode(true);
      bubble.appendChild(heading);
      if (content) bubble.appendChild(content);
      const follow = document.createElement('p');
      follow.className = 'sp-help__follow';
      follow.append('Not what you needed? ');
      const link = document.createElement('a');
      link.href = mailLink(question);
      link.textContent = 'Email us your question';
      follow.appendChild(link);
      bubble.appendChild(follow);
    } else {
      const text = document.createElement('p');
      text.textContent = "I couldn't find that in our answers. Send it to us and a real person will reply within 2 business days.";
      const link = document.createElement('a');
      link.className = 'sp-btn sp-btn--primary sp-btn--small';
      link.href = mailLink(question);
      link.textContent = 'Email your question';
      bubble.append(text, link);
    }
    answer.appendChild(bubble);
    answer.hidden = false;
    publish('sp_help_question', { matched: Boolean(entry) });
  }

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    const question = input ? input.value.trim() : '';
    if (!question) return;
    show(best(question), question);
  });

  root.querySelectorAll('[data-sp-help-topic]').forEach((chip) => {
    chip.addEventListener('click', () => {
      const id = chip.getAttribute('data-sp-help-topic');
      const entry = index.find((candidate) => candidate.item.dataset.spFaq === id) || null;
      show(entry, '');
    });
  });

  root.hidden = false;
  revealFab(fab);

  /* Shopify Inbox brings its own chat button; avoid two help buttons. */
  if (window.customElements) {
    customElements.whenDefined('shopify-chat').then(() => {
      root.hidden = true;
    });
  }
}

/* ------------------------------------------------------ cart drawer cards */

function initCartActions() {
  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const applyButton = target.closest('[data-sp-apply-code]');
    if (applyButton instanceof HTMLButtonElement) {
      event.preventDefault();
      applyButton.disabled = true;
      applyButton.setAttribute('aria-busy', 'true');
      const result = await applyCode(applyButton.dataset.spApplyCode || '');
      applyButton.disabled = false;
      applyButton.removeAttribute('aria-busy');
      if (result.reload) window.location.reload();
      return;
    }

    const prizeButton = target.closest('[data-sp-prize-add]');
    if (prizeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      let data = null;
      try {
        data = JSON.parse(prizeButton.dataset.spPrizeAdd || '{}');
      } catch (error) {
        return;
      }
      if (!data || !data.id || !data.code) return;
      prizeButton.disabled = true;
      prizeButton.setAttribute('aria-busy', 'true');
      const result = await applyCode(data.code, [{ merchandiseId: data.id, quantity: 1 }]);
      prizeButton.disabled = false;
      prizeButton.removeAttribute('aria-busy');
      if (!result.ok) {
        const box = prizeButton.closest('[data-sp-offers]')?.querySelector('[data-sp-error]');
        if (box) {
          box.textContent = result.message || 'That could not be added. Please try again.';
          box.hidden = false;
        }
      } else if (result.reload) {
        window.location.reload();
      }
    }
  });

  /* Delivery protection only makes sense with something to ship. */
  onCartUpdate(async () => {
    const cart = await fetchCart();
    if (!cart || !cart.items) return;
    const protection = cart.items.find((item) => item.handle === 'delivery-protection');
    if (!protection) return;
    const shippable = cart.items.some((item) => item.requires_shipping);
    if (!shippable || protection.quantity > 1) {
      await updateCart({ lines: [{ id: protection.key, quantity: shippable ? 1 : 0 }] });
    }
  });
}

/* ------------------------------------------------------------------ shared */

let eventsModule;
const cartListeners = [];

function onCartUpdate(listener) {
  cartListeners.push(listener);
}

async function listenToCart() {
  try {
    eventsModule = await import('@shopify/events');
  } catch (error) {
    eventsModule = null;
  }
  const names = eventsModule && eventsModule.StandardEvents;
  if (!names) return;
  let timer = null;
  const run = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => cartListeners.forEach((listener) => listener()), 400);
  };
  for (const name of [names.cartLinesUpdate, names.cartDiscountUpdate]) {
    if (!name) continue;
    document.addEventListener(name, (event) => {
      const promise = event && event.promise;
      if (promise && typeof promise.then === 'function') promise.then(run, () => {});
      else run();
    });
  }
}

function publish(name, data) {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (error) {
    /* analytics are optional */
  }
}

function init() {
  initWheel();
  initHelp();
  initCartActions();
  listenToCart();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}